import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { Welcome } from '../pages/Welcome';
import { useSession } from '../session';
import {
  BookIcon,
  CardIcon,
  ChartIcon,
  CheckIcon,
  ChevronUpDownIcon,
  GlobeIcon,
  HelpIcon,
  KeyIcon,
  LogoutIcon,
  LogsIcon,
  MailIcon,
  MenuIcon,
  PulseIcon,
  SettingsIcon,
  ShieldIcon,
  UsersIcon,
  WebhookIcon,
} from './icons';
import { useDismiss } from './ui';

const GROUPS = [
  [
    { to: '/emails', label: 'Emails', icon: MailIcon },
    { to: '/metrics', label: 'Metrics', icon: ChartIcon },
  ],
  [
    { to: '/domains', label: 'Domains', icon: GlobeIcon },
    { to: '/api-keys', label: 'API keys', icon: KeyIcon },
    { to: '/webhooks', label: 'Webhooks', icon: WebhookIcon },
    { to: '/logs', label: 'Logs', icon: LogsIcon },
  ],
  [
    { to: '/billing', label: 'Billing', icon: CardIcon },
    { to: '/settings', label: 'Settings', icon: SettingsIcon },
  ],
];

function TeamSwitcher() {
  const { me, team, switchTeam } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className="team-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar">{team.name.charAt(0).toUpperCase()}</span>
        <span className="name">
          {team.name}
          <span className="plan">
            {team.plan.charAt(0).toUpperCase() + team.plan.slice(1)} plan
          </span>
        </span>
        <ChevronUpDownIcon />
      </button>
      {open ? (
        <div className="menu below" role="menu">
          <div className="menu-label">Teams</div>
          {me.teams.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                if (t.id !== team.id) {
                  switchTeam(t.id);
                  window.location.assign('/emails');
                }
              }}
            >
              <span className="avatar sm">{t.name.charAt(0).toUpperCase()}</span>
              {t.name}
              {t.id === team.id ? <CheckIcon className="check" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function UserMenu() {
  const { me, logout } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div className="menu-wrap user" ref={ref}>
      <button
        type="button"
        className="user-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar sm" style={{ background: '#27272a', boxShadow: 'none' }}>
          {me.user.email.charAt(0).toUpperCase()}
        </span>
        <span>{me.user.email}</span>
      </button>
      {open ? (
        <div className="menu foot-menu" role="menu">
          <div className="menu-label">{me.user.email}</div>
          <a href="https://vaartix.com/docs/" target="_blank" rel="noreferrer" role="menuitem">
            <BookIcon />
            Documentation
          </a>
          <a href="https://vaartix.com/support" target="_blank" rel="noreferrer" role="menuitem">
            <HelpIcon />
            Support
          </a>
          <hr />
          <button type="button" role="menuitem" className="danger" onClick={() => void logout()}>
            <LogoutIcon style={{ color: 'var(--bad)' }} />
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The (?) button: where to get help. */
function HelpMenu() {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className="icon-btn"
        title="Help"
        aria-label="Help"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <HelpIcon />
      </button>
      {open ? (
        <div className="menu foot-menu help-menu" role="menu">
          <div className="menu-label">Help</div>
          <a href="https://vaartix.com/support" target="_blank" rel="noreferrer" role="menuitem">
            <HelpIcon />
            Support
          </a>
          <a href="https://status.vaartix.com" target="_blank" rel="noreferrer" role="menuitem">
            <PulseIcon />
            Status
          </a>
          <a href="https://vaartix.com/docs/" target="_blank" rel="noreferrer" role="menuitem">
            <BookIcon />
            Documentation
          </a>
          <hr />
          <a href="mailto:support@vaartix.com" role="menuitem">
            <MailIcon />
            support@vaartix.com
          </a>
        </div>
      ) : null}
    </div>
  );
}

const ADMIN_GROUP = [
  { to: '/admin', label: 'Admin', icon: ShieldIcon },
  { to: '/admin/invites', label: 'Invites', icon: UsersIcon },
];

export function Layout() {
  const { team } = useSession();
  if (!team.onboarded && team.role !== 'member') return <Welcome />;
  return <Shell />;
}

function Shell() {
  const { me, team } = useSession();
  const groups = me.isAdmin ? [...GROUPS, ADMIN_GROUP] : GROUPS;
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close the menu on navigation
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className={`shell${open ? ' menu-open' : ''}`}>
      <aside className="sidebar">
        <TeamSwitcher />
        <nav className="nav" aria-label="Dashboard">
          {groups.map((group, i) => (
            <div key={group[0]?.to}>
              {i > 0 ? <div className="nav-sep" /> : null}
              {group.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} end={to === '/admin'}>
                  <Icon />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <UserMenu />
          <a
            className="icon-btn"
            href="https://vaartix.com/docs/"
            target="_blank"
            rel="noreferrer"
            title="Documentation"
            aria-label="Documentation"
          >
            <BookIcon />
          </a>
          <HelpMenu />
        </div>
      </aside>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the menu also closes on navigation */}
      <main className="main" onClick={() => open && setOpen(false)}>
        <div className="mobile-bar">
          <button
            type="button"
            className="icon-btn"
            aria-label="Open menu"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <MenuIcon />
          </button>
          <img src="/vaartix-icon.svg" alt="" />
          vaartix
        </div>
        {team.status !== 'active' ? (
          <div className="banner" role="alert">
            <span>
              <strong>Sending is paused for {team.name}.</strong> Your bounce or complaint rate went
              above the safe limit. Check{' '}
              <NavLink to="/metrics" className="link">
                Metrics
              </NavLink>
              , then write to support@vaartix.com and we'll help you fix it.
            </span>
          </div>
        ) : null}
        {me.twoFactor?.required && !me.twoFactor.enabled ? (
          <div className="banner" role="alert">
            <span>
              <strong>Turn on two-factor authentication.</strong> It's required for admins: the
              admin panel stays locked until it's on.{' '}
              <NavLink to="/settings" className="link">
                Set it up in Settings
              </NavLink>
              .
            </span>
          </div>
        ) : null}
        <div className="page">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
